var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useMemo, useEffect, } from 'react';
import { useNavigate } from 'react-router-dom';
import { onSnapshot, collection, doc, query, where, } from 'firebase/firestore';
import { firebaseDb } from '../../firebase.config';
import useFirebaseGetDocs from '../hooks/useFirebaseGetDocs';
import { generateMapSessionCode, getUserId } from '../../utils';
import useFirebaseUpdateDoc from '../hooks/useFirebaseUpdateDoc';
import useFirebaseCreateDoc from '../hooks/useFirebaseCreateDoc';
var AppContext = createContext(undefined);
// Save an item to local storage
var saveToStorage = function (key, value) {
    try {
        localStorage.setItem(key, value);
        console.log('Saved to local storage:', key, value);
    }
    catch (error) {
        console.error('Error saving to local storage', error);
    }
};
// Retrieve an item from local storage
var retrieveFromStorage = function (key) {
    try {
        var value = localStorage.getItem(key);
        return value ? value : null;
    }
    catch (error) {
        console.error('Error retrieving from local storage', error);
        return null;
    }
};
export var useAppContext = function () {
    var context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
// Provider component to wrap your app
export var AppProvider = function (_a) {
    var _b, _c;
    var children = _a.children;
    var navigate = useNavigate();
    var fetchData = useFirebaseGetDocs().fetchData;
    var updateData = useFirebaseUpdateDoc().updateData;
    var createData = useFirebaseCreateDoc().createData;
    var _d = useState({
        snapshotListenerActive: false,
        isAuthenticated: false,
        mapSession: undefined,
        loading: false,
        user: null,
    }), componentState = _d[0], setComponentState = _d[1];
    var updateComponentState = function (newComponentState) {
        setComponentState(function (componentState) {
            return __assign(__assign({}, componentState), newComponentState);
        });
    };
    useEffect(function () {
        var _a;
        if (componentState.snapshotListenerActive &&
            ((_a = componentState === null || componentState === void 0 ? void 0 : componentState.mapSession) === null || _a === void 0 ? void 0 : _a.id)) {
            onSnapshot(doc(firebaseDb, 'map-session', componentState === null || componentState === void 0 ? void 0 : componentState.mapSession.id), function (mapSessionDoc) {
                var sessionData = mapSessionDoc.data();
                if (sessionData) {
                    console.log('Session updated:', sessionData);
                    updateComponentState(__assign(__assign({}, componentState), { mapSession: sessionData }));
                }
                else {
                    console.error('Session data is undefined');
                }
            });
        }
    }, [componentState.snapshotListenerActive, (_b = componentState.mapSession) === null || _b === void 0 ? void 0 : _b.id]);
    var updateMapSession = function (mapSession) {
        if (!(mapSession === null || mapSession === void 0 ? void 0 : mapSession.id))
            return;
        try {
            var docRef = doc(firebaseDb, 'map-session', mapSession.id);
            // Update the map session updatedDate
            mapSession.updatedDate = new Date();
            // Update the last activity date for the current user
            var currentUser = mapSession.users.find(function (u) {
                return u.userId ===
                    retrieveFromStorage("huddle-maps-user-id-".concat(mapSession.sessionId.toLowerCase()));
            });
            if (currentUser)
                currentUser.lastActiveDate = new Date();
            updateData(docRef, mapSession)
                .then(function (updatedDocData) {
                var mapSession = updatedDocData;
                updateComponentState(__assign(__assign({}, componentState), { mapSession: mapSession }));
            })
                .catch(function (err) {
                console.error('Error updating map session:', err);
            });
        }
        catch (error) {
            console.error('Error updating map session:', error);
        }
    };
    useMemo(function () {
        if ((componentState === null || componentState === void 0 ? void 0 : componentState.isAuthenticated) === true &&
            (componentState === null || componentState === void 0 ? void 0 : componentState.mapSession)) {
            console.log('Joined map session:', componentState.mapSession.sessionId);
            updateMapSession(componentState.mapSession);
        }
    }, [componentState === null || componentState === void 0 ? void 0 : componentState.isAuthenticated]);
    var startMapSession = function () {
        updateComponentState(__assign(__assign({}, componentState), { loading: true }));
        var newSessionCode = generateMapSessionCode();
        setTimeout(function () {
            console.log("Creating map session: ".concat(newSessionCode));
            try {
                // Create a new user for the session
                var user_1 = {
                    userId: getUserId() + newSessionCode,
                    joinDate: new Date(),
                    lastActiveDate: new Date(),
                };
                // Save the userId to local storage
                saveToStorage("huddle-maps-user-id-".concat(newSessionCode.toLowerCase()), user_1.userId);
                // Create a new session object
                var newMapSession = {
                    id: '',
                    createdDate: new Date(),
                    updatedDate: new Date(),
                    sessionId: newSessionCode,
                    users: [user_1],
                    mapData: {
                        pins: [],
                    },
                    votes: [],
                };
                // Create the new map session document
                createData('map-session', newMapSession)
                    .then(function (createdDocData) {
                    var createdMapSession = createdDocData;
                    updateComponentState(__assign(__assign({}, componentState), { snapshotListenerActive: true, mapSession: createdMapSession, isAuthenticated: true, loading: false, user: user_1 }));
                    navigate('/');
                })
                    .catch(function (err) {
                    console.error('Error creating map session:', err);
                    updateComponentState(__assign(__assign({}, componentState), { loading: false }));
                });
            }
            catch (err) {
                console.error('Error starting map session:', err);
                updateComponentState(__assign(__assign({}, componentState), { loading: false }));
            }
        }, 2000);
    };
    var joinMapSession = function (sessionId) {
        if (!sessionId)
            return;
        sessionId = sessionId.toUpperCase();
        updateComponentState(__assign(__assign({}, componentState), { loading: true }));
        setTimeout(function () {
            try {
                fetchData(query(collection(firebaseDb, 'map-session'), where('sessionId', '==', sessionId)))
                    .then(function (queryDocSnapshot) {
                    // If the session document exists, join the session
                    if (queryDocSnapshot && queryDocSnapshot.length > 0) {
                        var mapSession = queryDocSnapshot[0].data();
                        var user_2 = {
                            userId: getUserId() + mapSession.sessionId,
                            joinDate: new Date(),
                            lastActiveDate: new Date(),
                        };
                        // Get current userId for this map session from local storage
                        var currentUserId = retrieveFromStorage("huddle-maps-user-id-".concat(mapSession.sessionId.toLowerCase()));
                        if (currentUserId) {
                            user_2.userId = currentUserId;
                            var userExists = mapSession.users.find(function (u) { return u.userId === user_2.userId; });
                            if (!userExists)
                                mapSession.users.push(user_2);
                            // Save the userId to local storage
                            saveToStorage("huddle-maps-user-id-".concat(mapSession.sessionId.toLowerCase()), currentUserId);
                        }
                        else {
                            saveToStorage("huddle-maps-user-id-".concat(mapSession.sessionId.toLowerCase()), user_2.userId);
                            mapSession.users.push(user_2);
                        }
                        mapSession.id = queryDocSnapshot[0].id;
                        updateComponentState(__assign(__assign({}, componentState), { snapshotListenerActive: true, mapSession: mapSession, isAuthenticated: true, loading: false, user: user_2 }));
                        navigate('/');
                    }
                    else {
                        console.error("Session ".concat(sessionId, " not found"));
                        updateComponentState(__assign(__assign({}, componentState), { loading: false }));
                    }
                })
                    .catch(function (err) {
                    console.error('Error joining map session:', err);
                    updateComponentState(__assign(__assign({}, componentState), { loading: false }));
                });
            }
            catch (err) {
                console.error('Error joining map session:', err);
                updateComponentState(__assign(__assign({}, componentState), { loading: false }));
            }
        }, 2000);
    };
    var value = {
        user: (_c = componentState.user) !== null && _c !== void 0 ? _c : null,
        mapSession: componentState.mapSession,
        isAuthenticated: componentState.isAuthenticated,
        loading: componentState.loading,
        startSession: function () {
            startMapSession();
        },
        joinSession: function (sessionId) {
            joinMapSession(sessionId);
        },
        updateSession: function (mapSession) {
            updateMapSession(mapSession);
        },
    };
    return _jsx(AppContext.Provider, { value: value, children: children });
};
